import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
import systemParameter from '@/store/modules/systemParameter'
import modules from '@/store/modules'

const phoneUtil = PhoneNumberUtil.getInstance()

/**
 * Returns a reusable array of phone validation rules for Vuetify fields.
 * Uses t('phone.RULES.REQUIRED_MSG') and t('phone.RULES.INVALID_MSG') for messages.
 * @param t Translation function (e.g., this.$t or useI18n().t)
 */
export function getPhoneValidationRules(t: (key: string) => string) {
  return [
    (v: string) => !!v || t('phone.RULES.REQUIRED_MSG'),
    (v: string) => {
      try {
        return isValidPhoneNumber(v) || t('phone.RULES.INVALID_MSG')
      } catch {
        return t('phone.RULES.INVALID_MSG')
      }
    }
  ]
}

/**
 * Helper to parse a phone number and infer its region.
 * Returns { number, region } or null if invalid.
 */
function parseAndInferRegion(input: string) {
  if (typeof input !== 'string' || input.length === 0) return null
  try {
    // If the input starts with + or 00, treat as international, else default to US
    const isInternational = /^\s*(\+|00)/.test(input)
    const region = isInternational ? 'ZZ' : 'US'
    const number = phoneUtil.parseAndKeepRawInput(input, region)
    const inferredRegion = phoneUtil.getRegionCodeForNumber(number)
    return { number, region: inferredRegion }
  } catch {
    return null
  }
}

/**
 * Formats a phone number in the national format for its inferred region.
 * E.g., (415) 555-2671 for US.
 */
export const phoneFormatFilter = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) return ''
  const parsed = parseAndInferRegion(input)
  if (!parsed) return input
  try {
    return phoneUtil.format(parsed.number, PhoneNumberFormat.NATIONAL)
  } catch {
    return input
  }
}

/**
 * Formats a phone number in the international format (with country code).
 * E.g., +1 415-555-2671 for US.
 */
export const phoneFormatFilterInternational = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) return ''
  const parsed = parseAndInferRegion(input)
  if (!parsed) return input
  try {
    return phoneUtil.format(parsed.number, PhoneNumberFormat.INTERNATIONAL)
  } catch {
    return input
  }
}

/**
 * Validates if a given phone number string is a valid phone number.
 *
 * @param input - The phone number as a string, potentially including non-numeric characters.
 * @returns True if the phone number is valid, false otherwise.
 */
export const isValidPhoneNumber = (input: string): boolean => {
  if (typeof input !== 'string' || input.length === 0) return false
  const parsed = parseAndInferRegion(input)
  if (!parsed) return false
  try {
    return phoneUtil.isValidNumberForRegion(parsed.number, parsed.region)
  } catch {
    return false
  }
}

/**
 * Returns the raw, unformatted phone number (digits only, national number).
 */
export const phoneRaw = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) return ''
  const parsed = parseAndInferRegion(input)
  if (!parsed) return input.replace(/\D/g, '')
  try {
    return parsed.number.getNationalNumber().toString()
  } catch {
    return input.replace(/\D/g, '')
  }
}

/**
 * Removes the country code and returns the national format.
 */
export const stripCountryCode = (input: string): string => {
  if (typeof input !== 'string' || input.length === 0) return ''
  const parsed = parseAndInferRegion(input)
  if (!parsed) return input
  try {
    // Return just the national number as digits
    return parsed.number.getNationalNumber().toString()
  } catch {
    return input
  }
}

/**
 * Returns the priority phone number if the conditions for Elite are met.
 * Otherwise returns the default support number.
 */
export const getSupportNumber = (isElite: boolean): string => {
  if (isElite) {
    return systemParameter.elitePriorityPhoneNumber
  }
  return systemParameter.supportPhoneNumber || modules.auth.defaultCustomerSupportNumber
}
